<template>
  <div slot="body">
    <div class="request-batch-overlay">

      <div class="virtual-cards-section">
        <p class="description-text">Select one or more virtual cards to order a batch of physical cards.</p>

        <h5 style="font-weight: bold !important;">Virtual Cards</h5>
        <div class="select-all-row" v-if="allCardIds.length">
          <input type="checkbox" id="selectAllCards" v-model="selectAll" />
          <label for="selectAllCards">Select All</label>
        </div>
        <div class="virtual-cards-list" v-if="sortedFilteredCards && sortedFilteredCards.length">
          <div v-for="card in sortedFilteredCards" :key="card.Id" class="virtual-card-row">
            <input type="checkbox" :value="card.Id" v-model="selectedCards" />
            <span>{{ card.Holder.FirstName }} {{ card.Holder.LastName }} {{ card.CardNumber }}</span>
          </div>
        </div>
        <div class="virtual-cards-list" v-if="list && list.length">
           <div v-for="cardNode in list" :key="cardNode.data.Id" class="virtual-card-row">
              <input type="checkbox" :value="cardNode.data.Id" v-model="selectedCards" />
              <span>{{ cardNode.data.Holder }} {{ cardNode.data.CardNumber }}</span>
          </div>
        </div>
      </div>

      <p>Provide a mailing address to send the selected cards.</p>

      <div class="form-group">
        <label for="country">Country</label>
        <ed-select @input="(o) => mailingAddress.country = o"
                    placeholder="Select a country..."
                    :options="countries"
                    v-model="formData.country"
                    label="CountryName"
                    track-by="CountryID"
                    :openEndpoint='api.$.profile.getAllCountries'
                    @select="countrySelected($event)"
                    @open="fetchCountriesOnOpen"
                    @keyup="fetchCountries"
                    @close="restoreCountries"
                    :error="doDisplayCountryError"
                    :helptip-header="doDisplayCountryError && 'Error' || ''"
                    :helptip-body="doDisplayCountryError && 'Please select a country' || ''"
                    :disabled="disableControls"
                    name='supplier_country' />
      </div>
      <div class="form-group">
        <label for="name">Name</label>
        <ed-input @input="(o) => mailingAddress.name = o"
                  type="text"
                  v-model="name"
                  id="name"
                  placeholder="Name"
                  maxlength="40"
                  class="full-width" />
        <span class="error-message" v-if="errors.name">{{ errors.name }}</span>
      </div>
      <div class="form-group">
        <label for="address">Address</label>
        <ed-input @input="(o) => mailingAddress.line1 = o"
                  type="text"
                  v-model="address"
                  id="address"
                  placeholder="Address"
                  maxlength="26"
                  class="full-width" />
        <span class="error-message" v-if="errors.line1">{{ errors.line1 }}</span>
      </div>
      <div class="form-group">
        <ed-input @input="(o) => mailingAddress.line2 = o"
                  type="text"
                  field-label="Address Line 2"
                  v-model="addressLine2"
                  id="addressLine2"
                  placeholder="Address Line 2"
                  maxlength="26"
                  class="full-width" />
      </div>
      <div class="form-group">
        <label for="city">City</label>
        <ed-input @input="(o) => mailingAddress.city = o"
                  type="text"
                  v-model="city"
                  id="city"
                  placeholder="City"
                  maxlength="25"
                  class="full-width" />
        <span class="error-message" v-if="errors.city">{{ errors.city }}</span>
      </div>
      <div class="form-group">
        <div class="input-group">
          <div class="input-group-item">
            <label for="state">State</label>
            <ed-select @input="(o) => mailingAddress.state = o"
                        v-if="isUS()"
                        :options="USstates"
                        v-model="formData.state"
                        @open="fetchUSStatesOnOpen"
                        @keyup="fetchUSStates"
                        @close="restoreUSStates"
                        label="StateName"
                        track-by="StateID"
                        name='us_states' />
            <ed-select @input="(o) => mailingAddress.state = o"
                        v-if="isCanada()"
                        placeholder="Select a region"
                        :options="canadaStates"
                        v-model="formData.canadaState"
                        @open="fetchCanadaStatesOnOpen"
                        @keyup="fetchCanadaStates"
                        @close="restoreCanadaStates"
                        label="StateName"
                        :disabled="disableControls"
                        name='canada_states' />
            <ed-input @input="(o) => mailingAddress.state = o"
                      v-if="!formData.country || !isCanada() && !isUS()"
                      placeholder="Select a region"
                      v-model="formData.region"
                      :disabled="disableControls"
                      name='state_region' />
            <span class="error-message" v-if="errors.state">{{ errors.state }}</span>
          </div>
          <div class="input-group-item">
            <label for="zipCode">Zip Code</label>
            <ed-input @input="(o) => mailingAddress.zip = o"
                      type="text"
                      v-model="zipCode"
                      id="zipCode"
                      placeholder="Zip Code"
                      maxlength="5"
                      class="full-width" />
            <span class="error-message" v-if="errors.zip">{{ errors.zip }}</span>
          </div>
        </div>
      </div>
      <hr v-if="this.allowRushPhysicalCards">
      <div class="form-group"
           :class="{ 'is-disabled-field': isRushDisabled }"
           v-if="this.allowRushPhysicalCards">
        <label for="rushOrder">Rush Order - <span class="optional-text">optional</span></label>
        <p class="small">
          For a fee, physical cards can be sent via expedited shipping.
        </p>
        <div class="form-group" v-tooltip="rushDisabledReason">
          <input type="checkbox"
                 v-model="rushOrder"
                 id="rushOrder"
                 :disabled="isRushDisabled">
          <label class="small" for="rushOrder">Rush shipment of this physical card order</label>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  export default {
    data: function () {
      return {
        formData: {
          city: '',
          postalCode: '',
          originalCountryName: '',
          country: '',
          state: {
            StateID: 0,
            StateName: ''
          },
          canadaState: {
            StateID: 0,
            StateName: ''
          },
          region: ''
        },

        USstates: [],
        USoriginalStates: [],

        canadaStates: [],
        originalCanadaStates: [],

        countries: [],
        originalCountries: [],
        mailingAddress: {},
        addObjectUser: {},
        rushOrder: false,
        requireReceiptDescription: false,
        allowRushPhysicalCards: false,
        errors: {},
        selectedCards: []
      }
    },

    created() {
      this.resetFields()
      this.getDivisionSettings()
      this.populateFormData(this.formData)
      this.setSelectedCardsFromList()
    },

    props: {
      card: Object,
      divisionId: '',
      cardDivisionId: '',
      filteredCards: Array,
      list: Array,
      account: Object
    },

    watch: {
      list: {
        immediate: true,
        handler(newList) {
          this.setSelectedCardsFromList()
        }
      },
      isRushDisabled(disabled) {
        if (disabled) {
          this.rushOrder = false
        }
      }
    },

    computed: {
      sortedFilteredCards() {
        if (!this.filteredCards) return []
        return [...this.filteredCards].sort((a, b) => {
          const getDateFromString = (dateString) => {
            const match = /\/Date\((\d+)\)\//.exec(dateString)
            return match ? new Date(parseInt(match[1], 10)) : new Date()
          }
          return getDateFromString(b.CreatedDate) - getDateFromString(a.CreatedDate)
        })
      },
      allCardIds() {
        if (this.sortedFilteredCards && this.sortedFilteredCards.length) {
          return this.sortedFilteredCards.map(card => card.Id)
        }
        if (this.list && this.list.length) {
          return this.list.map(cardNode => cardNode.data.Id)
        }
        return []
      },
      selectAll: {
        get() {
          return this.allCardIds.length > 0 && this.selectedCards.length === this.allCardIds.length
        },
        set(checked) {
          this.selectedCards = checked ? [...this.allCardIds] : []
        }
      },
      underfundedAccountNames() {
        const items = this.list || []
        const names = items
          .filter(item => {
            const data = item && item.data
            if (!data || !this.selectedCards.includes(data.Id)) return false
            return typeof data.AccountBalance === 'number' && data.AccountBalance <= 0
          })
          .map(item => item.data.Division)
          .filter(Boolean)
        return Array.from(new Set(names))
      },
      isRushDisabled() {
        if (this.account && this.account.Available <= 0) {
          return true
        }
        return this.underfundedAccountNames.length > 0
      },
      rushDisabledReason() {
        if (this.account && this.account.Available <= 0) {
          return 'Insufficient funds available for rush shipping.'
        }
        if (this.underfundedAccountNames.length > 0) {
          return `The following accounts have insufficient funds available for rush shipping: ${this.underfundedAccountNames.join(', ')}.`
        }
        return ''
      }
    },

    methods: {
      async requestPhysicalCard(showModal) {
        try {
          const isValid = this.validateForm()
          if (!isValid) {
            return
          }

          const cardIds = this.selectedCards

          const response = await this.api.post(`evedpaycards/request-physical-card`, {
            preventError: true,
            Shipping: {
              IsRush: this.rushOrder,
              Name: this.mailingAddress.name,
              Address: {
                Country: { Id: 186 },
                Address1: this.mailingAddress.line1,
                Address2: this.mailingAddress.line2,
                City: this.mailingAddress.city,
                State: { Id: this.mailingAddress.state.StateID },
                Zip: this.mailingAddress.zip
              },
              CardIds: cardIds
            }
          })

          if (response.status === 200) {
            showModal.showModal = false
            this.$emit('success')
          } else {
            console.error('Error requesting physical card:', response)
          }
        } catch (error) {
          console.error('Exception while requesting physical card:', error)
          let userFriendlyErrorMessage = 'An unexpected error occurred. Please try again.'
          if (error.response && error.response.data) {
            const errorDataString = JSON.stringify(error.response.data)
            const firstBracketIndex = errorDataString.indexOf('[') // searching for the first bracket in the message
            const secondBracketIndex = errorDataString.indexOf('[', firstBracketIndex + 1) // when the first bracket is found, search for the second one
            if (secondBracketIndex !== -1) {
              const colonIndex = errorDataString.indexOf(':', secondBracketIndex) // after the second bracket is found, look for the colon -> from there the message for the user starts
              if (colonIndex !== -1) {
                const endOfMessagePeriod = errorDataString.indexOf('.', colonIndex) // end when the first period is found
                const endOfMessageParenthesis = errorDataString.indexOf('(', colonIndex) // or end when the first parethesis is found
                let endOfMessageIndex = errorDataString.length
                // min index if both are found
                if (endOfMessagePeriod !== -1 && endOfMessageParenthesis !== -1) {
                  endOfMessageIndex = Math.min(endOfMessagePeriod, endOfMessageParenthesis)
                  // if only period is found
                } else if (endOfMessagePeriod !== -1) {
                  endOfMessageIndex = endOfMessagePeriod
                  // if only parenthesis is found
                } else if (endOfMessageParenthesis !== -1) {
                  endOfMessageIndex = endOfMessageParenthesis
                }
                // extract and trim the detailed error message
                const detailedMessage = errorDataString.substring(colonIndex + 1, endOfMessageIndex).trim()
                userFriendlyErrorMessage = detailedMessage
                // remove any trailing parenthesis from the error message
                if (userFriendlyErrorMessage.endsWith('(')) {
                  userFriendlyErrorMessage = userFriendlyErrorMessage.slice(0, -1).trim()
                }
              }
            }
          }
          alert(userFriendlyErrorMessage)
        }
      },

      setSelectedCardsFromList() {
        this.selectedCards = (this.list || this.filteredCards || [])
          .map(cardNode => (cardNode && cardNode.data ? cardNode.data.Id : cardNode.Id))
      },

      async getDivisionSettings() {
        this.programs = []
        const divisionIDToUse = this.divisionId ? this.divisionId : this.cardDivisionId
        this.api.post('Settings/GetDivision', {
          divisionID: divisionIDToUse
        })
          .then(response => {
            if (response && response.data) {
              const responseData = JSON.parse(response.data.Data)
              const evedPayCardAccountSettingsObj = JSON.parse(responseData.evedPayCardAccountSettings)
              this.allowRushPhysicalCards = evedPayCardAccountSettingsObj.AllowRushPhysicalCards
            }
          })
          .catch(error => {
            console.warn(error)
          })
      },

      resetFields() {
        this.rushOrder = false
        this.mailingAddress = {}
        this.name = ''
        this.address = ''
        this.addressLine2 = ''
        this.city = ''
        this.formData.state = ''
        this.formData.canadaState = ''
        this.formData.region = ''
        this.zipCode = ''
      },

      fetchCountriesOnOpen() {
        if (this.countries.length) {
        } else {
          this.api.forms.post(
            this.api.$.profile.getAllCountries,
            {
              q: ''
            }
          ).then(r => {
            if (r && r.data && r.data.Data) {
              this.countries = r.data.Data
              this.originalCountries = r.data.Data
            }
          }
          ).catch(e => console.warn(e))
        }
      },

      fetchCountries(value) {
        this.counter || this.api.forms.post(
          this.api.$.profile.getAllCountries,
          {
            q: value || ''
          }
        ).then(r => {
          this.counter = 0
          if (r && r.data && r.data.Data) {
            this.countries = r.data.Data
          }
        }
        ).catch(e => {
          console.warn(e)
          this.counter = 0
        })
        this.counter = 1
      },

      async fetchUserDivisions() {
        this.divisions = []
        if (this.accountId === '') {
          this.api.get(this.api.$.evedpaycard.division)
            .then(response => {
              if (response && response.data) {
                for (var account of response.data) {
                  this.divisions.push({
                    Id: account.DivisionId,
                    Name: account.DivisionName
                  })
                }
              }
            })
            .catch(error => {
              console.warn(error)
            })
        }
      },

      restoreCountries() {
        if (this.counter === 0) {
          this.countries = utils.copy(this.originalCountries)
        }
      },

      isUS() {
        return this.formData.country && this.formData.country.CountryName === 'United States'
      },
      GetStatesUSURL() {
        return this.api.$.profile.getAllStates
      },

      isCanada() {
        return this.formData.country && this.formData.country.CountryName === 'Canada'
      },
      GetStatesCanadaURL() {
        return this.api.$.profile.getCanadaStates
      },

      // Fetch States on OPEN
      fetchUSStatesOnOpen() {
        var url = this.GetStatesUSURL()
        this.fetchStatesOnOpen(this.USstates, url)
      },
      fetchCanadaStatesOnOpen() {
        var url = this.GetStatesCanadaURL()
        this.fetchStatesOnOpen(this.canadaStates, url)
      },
      fetchStatesOnOpen(states, url) {
        if (states.length && (this.isCanada() || this.isUS())) {
        } else {
          this.api.forms.post(
            url,
            {
              q: ''
            }
          ).then(r => {
            if (r && r.data && r.data.Data) {
              this.mapStatesDataOnOpen(r.data.Data)
            }
          }
          ).catch(e => console.warn(e))
        }
      },
      mapStatesDataOnOpen(data) {
        if (this.isUS()) {
          this.USstates = this.USoriginalStates = data
        } else {
          this.canadaStates = this.originalCanadaStates = data
        }
      },

      // Fetch States on search text
      fetchUSStates(value) {
        var url = this.GetStatesUSURL()
        this.fetchStates(value, url)
      },
      fetchCanadaStates(value) {
        var url = this.GetStatesCanadaURL()
        this.fetchStates(value, url)
      },
      fetchStates(value, url) {
        this.counter || this.api.forms.post(
          url,
          {
            q: value || ''
          }
        ).then(r => {
          this.counter = 0
          if (r && r.data && r.data.Data) {
            this.mapStatesDataOnFetch(r.data.Data)
          }
        }
        ).catch(e => {
          console.warn(e)
          this.counter = 0
        })
        this.counter = 1
      },
      mapStatesDataOnFetch(data) {
        if (this.isUS()) {
          this.USstates = data
        } else {
          this.canadaStates = data
        }
      },

      // Restore States
      restoreUSStates() {
        this.restoreStates(this.USstates, this.USoriginalStates)
      },
      restoreCanadaStates() {
        this.restoreStates(this.canadaStates, this.originalCanadaStates)
      },
      restoreStates(states, originalStates) {
        if (this.counter === 0) {
          this.states = utils.copy(originalStates)
        }
      },
      populateFormData(data) {
        if (data.CompanyName !== null) {
          this.formData = {
            city: data.City,
            postalCode: data.Zip,
            originalCountryName: data.CountryName,
            country: {
              'CountryID': 186,
              'CountryName': 'United States'
            },
            state: {
              StateID: data.isCanada === 'False' && data.StateID || 0,
              StateName: data.isCanada === 'False' && data.State || ''
            },
            canadaState: {
              StateID: data.isCanada === 'True' && data.StateID || 0,
              StateName: data.isCanada === 'True' && data.State || ''
            },
            region: data.Region
          }
          this.disableControls = true
        } else {
          this.disableControls = false
        }
      },
      countrySelected(e) {
        this.doDisplayCountryError = false
        this.resetState(e.CountryName)
      },

      resetState(countryName) {
        if (this.originalCountryName !== '' && this.originalCountryName !== countryName) {
          this.formData.state = {
            StateID: 0,
            StateName: ''
          }
          this.formData.canadaState = {
            StateID: 0,
            StateName: ''
          }
          this.formData.region = ''
        } else {
          this.originalCountryName = countryName
        }
      },

      validateForm() {
        this.errors = {}

        if (!this.mailingAddress.name || !this.mailingAddress.name.trim()) {
          this.errors.name = 'Name is required.'
        }

        if (!this.mailingAddress.line1 || !this.mailingAddress.line1.trim()) {
          this.errors.line1 = 'Address is required.'
        }

        if (!this.mailingAddress.city || !this.mailingAddress.city.trim()) {
          this.errors.city = 'City is required.'
        }

        if (this.isUS() && (!this.mailingAddress.state || !this.mailingAddress.state.StateID)) {
          this.errors.state = 'State is required.'
        }

        if (!this.mailingAddress.zip || !this.mailingAddress.zip.trim()) {
          this.errors.zip = 'Zip Code is required.'
        }

        const isValid = Object.keys(this.errors).length === 0
        return isValid
      }
    }
  }
</script>

<style lang="scss" scoped>
  .form-group {
    margin-bottom: 10px;
  }

  .virtual-cards-section {
    margin-bottom: 20px;
  }

  .select-all-row {
    display: flex;
    align-items: center;
    margin-bottom: 6px;

    input {
      margin-right: 10px;
      width: 16px;
      height: 16px;
      margin-top: 0px;
    }

    label {
      font-size: 14px;
      line-height: 16px;
      cursor: pointer;
      margin-bottom: 0;
    }
  }

  .virtual-cards-list {
    border: 1px solid #ccc;
    padding: 10px;
    max-height: 150px;
    overflow-y: auto;
  }

  .virtual-card-row {
    display: flex;
    align-items: center;
    margin-bottom: 5px;
  }

  .virtual-card-row input {
    margin-right: 10px;
    width: 16px;
    height: 16px;
    margin-top: 0px;
  }

  .virtual-card-row span {
    font-size: 14px;
    line-height: 16px;
  }

  .full-width {
    width: 100%;
  }

  .error {
    color: red;
  }

  .form-group label {
    vertical-align: middle;
  }

  .input-group {
    display: flex;
    flex-wrap: wrap;
  }

  .input-group-item {
    flex: 1;
    margin-right: 10px;
  }

  .small-italic {
    font-size: smaller;
    font-style: italic;
  }

  .checkbox-label-container {
    align-items: center;
  }

  ::-webkit-input-placeholder {
    font-size: 12px;
  }

  :-moz-placeholder {
    font-size: 12px;
  }

  ::-moz-placeholder {
    font-size: 12px;
  }

  :-ms-input-placeholder {
    font-size: 12px;
  }

  .optional-text {
    font-style: italic;
    opacity: 0.6;
  }

  .request-batch-overlay {
    background-color: white;
    padding: 20px;
    border: 1px solid #ccc;
    border-radius: 4px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  }

  .is-disabled-field {
    opacity: 0.5;
    cursor: not-allowed;

    label {
      cursor: not-allowed;
    }
  }

  .error-message {
    color: red;
    font-size: 0.8rem;
  }

  .description-text {
    font-size: 14px;
    color: #555;
  }
</style>



// WEBPACK FOOTER //
// src/components/_modules/Programs/Card-list/RequestPhysicalBatch.vue